import { computed, ref, watch } from 'vue';

import type { BookingOptionUnit } from '@/types/products';

import { useDropdown } from '@/composables/useDropdown';
import { useBookingWidgetContext } from '@/types/booking-widget-context';
import { getLocalizedLabel } from '@/utils/utils';

export function useUnitsDropdown() {
  const context = useBookingWidgetContext();
  const isMobile = context.bookingWidget.isMobile;

  const isOpen = ref(false);
  const dropdown = ref<HTMLElement>();
  const selectedUnits = ref<Map<string, number>>(new Map());
  const tempSelectedUnits = ref<Map<string, number>>(new Map());

  watch(() => context.bookingWidget.selectedOption.value, (newOption) => {
    if (newOption) {
      const newDefault = newOption.units.find(unit => unit.isMainUnit) ?? newOption.units[0];
      if (newDefault) {
        const newMap = new Map();
        newMap.set(newDefault.id, 1);
        selectedUnits.value = newMap;
        tempSelectedUnits.value = new Map(newMap);
        context.bookingWidget.handleUnitsSelected(selectedUnits.value);
      }
    }
  }, { immediate: true });

  function getUnitLabel(unit: BookingOptionUnit): string {
    return getLocalizedLabel(unit.labels, unit.label, context.bookingWidget.currentLocale.value);
  }

  function getLocalizedDescription(unit: BookingOptionUnit) {
    if (!unit.descriptions || unit.descriptions.length === 0)
      return null;
    const found = unit.descriptions.find(d => d.lang === context.bookingWidget.currentLocale.value && d.content.trim() !== '');
    const fallback = unit.descriptions.find(d => d.content.trim() !== '');
    const content = found?.content ?? fallback?.content ?? null;
    return content && content.trim() !== '' ? content : null;
  }

  function hasDescription(unit: BookingOptionUnit) {
    if (!unit.descriptions || unit.descriptions.length === 0)
      return false;
    return unit.descriptions.some(d => d.content.trim() !== '');
  }

  const totalCount = computed(() => {
    let total = 0;
    selectedUnits.value.forEach(qty => total += qty);
    return total;
  });

  const tempTotalCount = computed(() => {
    let total = 0;
    tempSelectedUnits.value.forEach(qty => total += qty);
    return total;
  });

  const summaryText = computed(() => {
    const parts: string[] = [];
    context.bookingWidget.selectedOption.value?.units.forEach((unit) => {
      const qty = selectedUnits.value.get(unit.id) || 0;
      if (qty > 0) {
        const label = getUnitLabel(unit);
        parts.push(`${qty} ${label.toLowerCase()}`);
      }
    });
    return parts.join(', ');
  });

  const isValid = computed(() => {
    if (tempTotalCount.value < 1)
      return false;

    const units = context.bookingWidget.selectedOption.value?.units;
    if (!units)
      return false;

    for (const unit of units) {
      const qty = tempSelectedUnits.value.get(unit.id) || 0;
      const maxQty = unit.restrictions?.maxQuantity;

      if (qty > 0) {
        const minQty = unit.restrictions?.minQuantity || 0;
        if (qty < minQty) {
          return false;
        }

        if (unit.restrictions?.accompaniedBy) {
          const accompaniedQty = tempSelectedUnits.value.get(unit.restrictions.accompaniedBy) || 0;
          if (accompaniedQty < 1) {
            return false;
          }
        }
      }

      if (maxQty && maxQty > 0 && qty > maxQty) {
        return false;
      }
    }

    return true;
  });

  const { openDropdown, closeDropdown } = useDropdown({
    isOpen,
    dropdownRef: dropdown,
    isMobile,
    onOpen: () => {
      tempSelectedUnits.value = new Map(selectedUnits.value);
    },
    onClose: () => {
      if (isValid.value) {
        selectedUnits.value = new Map(tempSelectedUnits.value);
        context.bookingWidget.handleUnitsSelected(selectedUnits.value);
      }
    },
  });

  function canIncrementUnit(unit: BookingOptionUnit) {
    const currentQty = tempSelectedUnits.value.get(unit.id) || 0;
    const maxQty = unit.restrictions?.maxQuantity;

    if (maxQty && maxQty > 0 && currentQty >= maxQty) {
      return false;
    }

    if (unit.restrictions?.accompaniedBy) {
      const accompaniedQty = tempSelectedUnits.value.get(unit.restrictions.accompaniedBy) || 0;
      if (accompaniedQty < 1) {
        return false;
      }
    }

    return true;
  }

  function incrementUnit(unit: BookingOptionUnit) {
    if (canIncrementUnit(unit)) {
      const currentQty = tempSelectedUnits.value.get(unit.id) || 0;
      tempSelectedUnits.value.set(unit.id, currentQty + 1);
    }
  }

  function decrementUnit(unit: BookingOptionUnit) {
    const currentQty = tempSelectedUnits.value.get(unit.id) || 0;
    if (currentQty > 0) {
      tempSelectedUnits.value.set(unit.id, currentQty - 1);
    }
  }

  function applySelection() {
    if (!isValid.value)
      return;
    selectedUnits.value = new Map(tempSelectedUnits.value);
    context.bookingWidget.handleUnitsSelected(selectedUnits.value);
    closeDropdown();
  }

  return {
    isMobile,
    isOpen,
    dropdown,
    selectedUnits,
    tempSelectedUnits,
    totalCount,
    tempTotalCount,
    summaryText,
    isValid,
    units: computed(() => context.bookingWidget.selectedOption.value?.units ?? []),
    getUnitLabel,
    getLocalizedDescription,
    hasDescription,
    openDropdown,
    closeDropdown,
    canIncrementUnit,
    incrementUnit,
    decrementUnit,
    applySelection,
  };
}
